import { createFileRoute, useNavigate } from "@tanstack/react-router";
import React from "react";
import SINGLE_RULESET_CONTEXT from "src/context/internal/ruleset";
import { useTranslation } from "react-i18next";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/base/table";
import { TrashIcon } from "@heroicons/react/20/solid";
import { LinkIcon } from "@heroicons/react/24/solid";
import { Button } from "src/components/base/button";
import { EmptyState } from "src/components/base/empty-state";
import { ApiInternal } from "src/api/api_internal";
import { useForm } from "@tanstack/react-form";
import {
    CreateAccessRuleSrc,
    FullAccessRuleSrc,
    GetRulesetResponseInstance,
} from "src/api/generated/frontend_internal_v1";
import Form from "src/components/base/form";
import { ErrorMessage, Field, Fieldset, Label, RequiredLabel } from "src/components/base/fieldset";
import { Combobox, ComboboxOption } from "src/components/base/combobox";
import ACCESS_RULE_CONTEXT from "src/context/internal/access-rule";
import clsx from "clsx";
import { TagBadge } from "src/components/badges/tag";
import { GroupBadge } from "src/components/badges/group";
import { DeviceGroupBadge } from "src/components/badges/device-group";
import { SubnetBadge } from "src/components/badges/subnet";
import { Badge } from "src/components/base/badge";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "src/components/base/dialog";
import { notify } from "src/components/base/toast";
import { LocalTab, TabMenu } from "src/components/base/tab-menu";

/** Which axis (src or dst) is the visual grouping built around */
type GroupBy = "src" | "dst";

export const Route = createFileRoute("/_admin/a/rulesets/$rulesetId/_ruleset/instances")({
    component: RouteComponent,
    validateSearch: (search: Record<string, unknown>): InstancesSearch => {
        return {
            group_by: search?.group_by === "dst" ? "dst" : "src",
            filter_src: parseSrcOrDst(search.filter_src),
            filter_dst: parseSrcOrDst(search.filter_dst),
        };
    },
});

/** Search parameters for the instances list */
type InstancesSearch = {
    /** Which axis is the visual grouping built around */
    group_by: GroupBy;
    /** Filter restricting results to instances with this source */
    filter_src: CreateAccessRuleSrc | null;
    /** Filter restricting results to instances with this destination */
    filter_dst: CreateAccessRuleSrc | null;
};

/** One row in the grouped view — a bucket of instances sharing the same `src` (or `dst`, when grouping by destination) */
type InstanceRow = {
    /** Stable key — `${axis.type}:${axis.value.uuid}` */
    key: string;
    /** The src or dst all members of this row share */
    axis: FullAccessRuleSrc;
    /** The instances in this row, already sorted by the secondary axis */
    members: Array<GetRulesetResponseInstance>;
};

/** Displays a single ruleset's instances */
function RouteComponent() {
    const [t] = useTranslation("admin-ruleset");
    const [tg] = useTranslation();

    const { ruleset, reset: resetRuleset } = React.useContext(SINGLE_RULESET_CONTEXT);
    const accessRuleCtx = React.useContext(ACCESS_RULE_CONTEXT);
    const search = Route.useSearch();
    const navigate = useNavigate();

    const options: Array<SrcOrDst> = [
        ...accessRuleCtx.options.groups.map(({ name, uuid }) => ({ type: "Group" as const, name, value: uuid })),
        ...accessRuleCtx.options.tags.map(({ name, uuid }) => ({ type: "Tag" as const, name, value: uuid })),
        ...accessRuleCtx.options.subnets.map(({ name, uuid }) => ({ type: "Subnet" as const, name, value: uuid })),
        ...accessRuleCtx.options.device_groups.map(({ name, uuid }) => ({
            type: "DeviceGroup" as const,
            name,
            value: uuid,
        })),
    ];

    const filterSrcOptions = React.useMemo(() => collectFilterOptions(ruleset.instances, "src"), [ruleset.instances]);
    const filterDstOptions = React.useMemo(() => collectFilterOptions(ruleset.instances, "dst"), [ruleset.instances]);

    const [srcSearch, setSrcSearch] = React.useState("");
    const [dstSearch, setDstSearch] = React.useState("");
    const [filterSrcSearch, setFilterSrcSearch] = React.useState("");
    const [filterDstSearch, setFilterDstSearch] = React.useState("");

    const [openDelete, setOpenDelete] = React.useState<GetRulesetResponseInstance | undefined>(undefined);
    const [openDeleteRow, setOpenDeleteRow] = React.useState<InstanceRow | undefined>(undefined);

    const form = useForm({
        defaultValues: {
            src: null as SrcOrDst | null,
            dst: null as SrcOrDst | null,
        },
        validators: {
            onSubmitAsync: async ({ value }) => {
                if (value.dst === null) {
                    return {
                        fields: {
                            dst: t("error.field-is-required"),
                        },
                    };
                }

                if (value.src === null) {
                    return {
                        fields: {
                            src: t("error.field-is-required"),
                        },
                    };
                }

                await ApiInternal.rulesetInstances.create({ ruleset: ruleset.uuid, src: value.src, dst: value.dst });
                await resetRuleset();
            },
        },
    });

    const filterSrcValue = lookupOption(filterSrcOptions, search.filter_src);
    const filterDstValue = lookupOption(filterDstOptions, search.filter_dst);

    const setSearch = async (next: Partial<InstancesSearch>) => {
        await navigate({
            to: "/a/rulesets/$rulesetId/instances",
            params: { rulesetId: ruleset.uuid },
            search: {
                group_by: search.group_by,
                filter_src: search.filter_src,
                filter_dst: search.filter_dst,
                ...next,
            },
        });
    };

    /**
     * Delete the given instance
     */
    const deleteInstance = async () => {
        if (!openDelete) return;
        await ApiInternal.rulesetInstances.delete(openDelete.uuid);
        await resetRuleset();
        notify.success(t("toast.instance-deleted"));
    };

    /**
     * Delete every instance in the currently confirmed row
     */
    const deleteRow = async () => {
        if (!openDeleteRow) return;
        await Promise.all(openDeleteRow.members.map((m) => ApiInternal.rulesetInstances.delete(m.uuid)));
        await resetRuleset();
        notify.success(t("toast.row-deleted"));
    };

    const rows = React.useMemo(
        () => buildRows(ruleset.instances, search),
        [ruleset.instances, search.filter_src, search.filter_dst, search.group_by],
    );

    const axisHeader = search.group_by === "src" ? t("label.source") : t("label.destination");
    const memberHeader = search.group_by === "src" ? t("label.destination") : t("label.source");

    return (
        <>
            <Form
                className={clsx("mb-10 rounded-lg border p-2", "bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800/50")}
                onSubmit={form.handleSubmit}
            >
                <Fieldset className={"grid w-full grid-cols-[1fr_1fr_1fr] gap-x-6 gap-y-2 px-6 py-2"}>
                    <form.Field name={"src"}>
                        {(fieldApi) => (
                            <>
                                <RequiredLabel className={"order-1"}>{t("label.source")}</RequiredLabel>
                                <Combobox
                                    className={"order-4 !mt-0"}
                                    value={fieldApi.state.value}
                                    onChange={(e) => fieldApi.handleChange(e)}
                                    onClose={() => setSrcSearch("")}
                                    invalid={fieldApi.state.meta.errors.length > 0}
                                    queryDisplay={(q) => (q ? `${TYPE_TO_BADGE[q.type]}:${q.name}` : "")}
                                    onQueryChange={(q) => setSrcSearch(q)}
                                >
                                    {options
                                        .filter(({ type, name }) =>
                                            name
                                                .toLowerCase()
                                                .includes(srcSearch.toLowerCase().replace(TYPE_TO_REGEX[type], "")),
                                        )
                                        .map((option) => (
                                            <ComboboxOption value={option} key={`${option.type}${option.value}`}>
                                                <Label>{`${TYPE_TO_BADGE[option.type]}:${option.name}`}</Label>
                                            </ComboboxOption>
                                        ))}
                                </Combobox>

                                {fieldApi.state.meta.errors.map((err) => (
                                    <ErrorMessage>{err}</ErrorMessage>
                                ))}
                            </>
                        )}
                    </form.Field>

                    <form.Field name={"dst"}>
                        {(fieldApi) => (
                            <>
                                <RequiredLabel className={"order-2"}>{t("label.destination")}</RequiredLabel>
                                <Combobox
                                    className={"order-5 !mt-0"}
                                    value={fieldApi.state.value}
                                    onChange={(e) => fieldApi.handleChange(e)}
                                    onClose={() => setDstSearch("")}
                                    invalid={fieldApi.state.meta.errors.length > 0}
                                    queryDisplay={(q) => (q ? `${TYPE_TO_BADGE[q.type]}:${q.name}` : "")}
                                    onQueryChange={(q) => setDstSearch(q)}
                                >
                                    {options
                                        .filter(({ type, name }) =>
                                            name
                                                .toLowerCase()
                                                .includes(dstSearch.toLowerCase().replace(TYPE_TO_REGEX[type], "")),
                                        )
                                        .map((option) => (
                                            <ComboboxOption value={option} key={`${option.type}${option.value}`}>
                                                <Label>{`${TYPE_TO_BADGE[option.type]}:${option.name}`}</Label>
                                            </ComboboxOption>
                                        ))}
                                </Combobox>

                                {fieldApi.state.meta.errors.map((err) => (
                                    <ErrorMessage>{err}</ErrorMessage>
                                ))}
                            </>
                        )}
                    </form.Field>

                    <div className={"order-3"}></div>
                    <div className={"order-6 flex items-end justify-end"}>
                        <Button type={"submit"}>{t("button.create")}</Button>
                    </div>
                </Fieldset>
            </Form>

            {ruleset.instances.length === 0 ? (
                <EmptyState
                    icon={<LinkIcon />}
                    title={t("heading.no-instances")}
                    description={t("description.no-instances")}
                />
            ) : (
                <>
                    <Fieldset className={"mb-6 grid w-full grid-cols-2 gap-x-6 gap-y-2"}>
                        <Field>
                            <Label>{t("label.filter-src")}</Label>
                            <Combobox
                                value={filterSrcValue}
                                onChange={(option) => setSearch({ filter_src: option ? toCreateSrc(option) : null })}
                                onClose={() => setFilterSrcSearch("")}
                                queryDisplay={(q) => (q ? `${TYPE_TO_BADGE[q.type]}:${q.name}` : t("label.show-all"))}
                                onQueryChange={(q) => setFilterSrcSearch(q)}
                            >
                                {[
                                    <ComboboxOption<SrcOrDst | null> value={null} key={"__all__"}>
                                        <Label>{t("label.show-all")}</Label>
                                    </ComboboxOption>,
                                    ...filterSrcOptions
                                        .filter(({ type, name }) =>
                                            name
                                                .toLowerCase()
                                                .includes(
                                                    filterSrcSearch.toLowerCase().replace(TYPE_TO_REGEX[type], ""),
                                                ),
                                        )
                                        .map((option) => (
                                            <ComboboxOption<SrcOrDst | null>
                                                value={option}
                                                key={`${option.type}${option.value}`}
                                            >
                                                <Label>{`${TYPE_TO_BADGE[option.type]}:${option.name}`}</Label>
                                            </ComboboxOption>
                                        )),
                                ]}
                            </Combobox>
                        </Field>
                        <Field>
                            <Label>{t("label.filter-dst")}</Label>
                            <Combobox
                                value={filterDstValue}
                                onChange={(option) => setSearch({ filter_dst: option ? toCreateSrc(option) : null })}
                                onClose={() => setFilterDstSearch("")}
                                queryDisplay={(q) => (q ? `${TYPE_TO_BADGE[q.type]}:${q.name}` : t("label.show-all"))}
                                onQueryChange={(q) => setFilterDstSearch(q)}
                            >
                                {[
                                    <ComboboxOption<SrcOrDst | null> value={null} key={"__all__"}>
                                        <Label>{t("label.show-all")}</Label>
                                    </ComboboxOption>,
                                    ...filterDstOptions
                                        .filter(({ type, name }) =>
                                            name
                                                .toLowerCase()
                                                .includes(
                                                    filterDstSearch.toLowerCase().replace(TYPE_TO_REGEX[type], ""),
                                                ),
                                        )
                                        .map((option) => (
                                            <ComboboxOption<SrcOrDst | null>
                                                value={option}
                                                key={`${option.type}${option.value}`}
                                            >
                                                <Label>{`${TYPE_TO_BADGE[option.type]}:${option.name}`}</Label>
                                            </ComboboxOption>
                                        )),
                                ]}
                            </Combobox>
                        </Field>
                    </Fieldset>

                    <TabMenu>
                        <span className={"mr-2 self-center text-sm/5 text-zinc-600 dark:text-zinc-400"}>
                            {t("label.group-by")}
                        </span>
                        <LocalTab active={search.group_by === "src"} onClick={() => setSearch({ group_by: "src" })}>
                            {t("label.group-by-src")}
                        </LocalTab>
                        <LocalTab active={search.group_by === "dst"} onClick={() => setSearch({ group_by: "dst" })}>
                            {t("label.group-by-dst")}
                        </LocalTab>
                    </TabMenu>

                    <Table dense={true} className={"mt-4"}>
                        <TableHead>
                            <TableRow>
                                <TableHeader>{axisHeader}</TableHeader>
                                <TableHeader>{memberHeader}</TableHeader>
                                <TableHeader className={"w-0"}>
                                    <span className={"sr-only"}>{tg("accessibility.actions")}</span>
                                </TableHeader>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row) => (
                                <TableRow key={row.key}>
                                    <TableCell className={"align-top"}>{renderBadge(row.axis)}</TableCell>
                                    <TableCell>
                                        <div className={"flex flex-wrap gap-1.5"}>
                                            {row.members.map((m) => {
                                                const other = search.group_by === "src" ? m.dst : m.src;
                                                return (
                                                    <React.Fragment key={m.uuid}>
                                                        {renderBadge(other, () => setOpenDelete(m))}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </div>
                                    </TableCell>
                                    <TableCell className={"align-top"}>
                                        <Button
                                            plain={true}
                                            onClick={() => setOpenDeleteRow(row)}
                                            aria-label={t("button.delete-row")}
                                        >
                                            <TrashIcon />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </>
            )}

            {openDelete && (
                <Dialog open={true} onClose={() => setOpenDelete(undefined)}>
                    <DialogTitle>{t("heading.delete-instance")}</DialogTitle>
                    <DialogBody>
                        <div className={"flex flex-wrap items-center gap-2"}>
                            {renderBadge(openDelete.src)}
                            <span aria-hidden={"true"} className={"text-zinc-500 dark:text-zinc-400"}>
                                →
                            </span>
                            {renderBadge(openDelete.dst)}
                        </div>
                    </DialogBody>
                    <DialogActions>
                        <Button onClick={() => setOpenDelete(undefined)}>{tg("button.cancel")}</Button>
                        <Button
                            color={"red"}
                            onClick={async () => {
                                await deleteInstance();
                                setOpenDelete(undefined);
                            }}
                        >
                            {t("button.delete-instance")}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}

            {openDeleteRow && (
                <Dialog open={true} onClose={() => setOpenDeleteRow(undefined)}>
                    <DialogTitle>{t("heading.delete-row")}</DialogTitle>
                    <DialogBody>
                        <div className={"flex flex-wrap items-center gap-2"}>
                            {search.group_by === "src" ? (
                                <>
                                    {renderBadge(openDeleteRow.axis)}
                                    <span aria-hidden={"true"} className={"text-zinc-500 dark:text-zinc-400"}>
                                        →
                                    </span>
                                    <Badge color={"zinc"}>*</Badge>
                                </>
                            ) : (
                                <>
                                    <Badge color={"zinc"}>*</Badge>
                                    <span aria-hidden={"true"} className={"text-zinc-500 dark:text-zinc-400"}>
                                        →
                                    </span>
                                    {renderBadge(openDeleteRow.axis)}
                                </>
                            )}
                        </div>
                    </DialogBody>
                    <DialogActions>
                        <Button onClick={() => setOpenDeleteRow(undefined)}>{tg("button.cancel")}</Button>
                        <Button
                            color={"red"}
                            onClick={async () => {
                                await deleteRow();
                                setOpenDeleteRow(undefined);
                            }}
                        >
                            {t("button.delete-row")}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </>
    );
}

/** Extended {@link CreateAccessRuleSrc} which includes a display name */
type SrcOrDst = CreateAccessRuleSrc & {
    /** The tag's, group's, subnet's or device group's display name */
    name: string;
};

const TYPE_ORDER: Record<FullAccessRuleSrc["type"], number> = {
    Group: 0,
    Tag: 1,
    Subnet: 2,
    DeviceGroup: 3,
};

const TYPE_TO_BADGE: Record<CreateAccessRuleSrc["type"], string> = {
    DeviceGroup: "device-group",
    Group: "group",
    Subnet: "subnet",
    Tag: "tag",
};

const TYPE_TO_REGEX: Record<CreateAccessRuleSrc["type"], RegExp> = {
    DeviceGroup: /^device-group:/,
    Group: /^group:/,
    Subnet: /^subnet:/,
    Tag: /^tag:/,
};

/**
 * Parse a {@link CreateAccessRuleSrc} from a URL search-param value
 *
 * @param raw the raw value to validate
 * @returns the validated `CreateAccessRuleSrc`, or `null` for absent or invalid input
 */
function parseSrcOrDst(raw: unknown): CreateAccessRuleSrc | null {
    if (raw == null) return null;
    if (typeof raw !== "object") return null;
    const candidate = raw as { type?: unknown; value?: unknown };
    if (typeof candidate.value !== "string") return null;
    switch (candidate.type) {
        case "Group":
        case "Tag":
        case "Subnet":
        case "DeviceGroup":
            return { type: candidate.type, value: candidate.value };
        default:
            return null;
    }
}

/**
 * Find the {@link SrcOrDst} (incl. display name) matching the URL filter value
 *
 * @param options the list of known options
 * @param src the URL filter value
 * @returns the matching option, or `null` if `src` is `null` or has no match in `options`
 */
function lookupOption(options: Array<SrcOrDst>, src: CreateAccessRuleSrc | null): SrcOrDst | null {
    if (src === null) return null;
    return options.find((o) => o.type === src.type && o.value === src.value) ?? null;
}

/**
 * Strip the display name back off a {@link SrcOrDst} to get a plain {@link CreateAccessRuleSrc}
 *
 * @param option the option to strip
 * @returns a `CreateAccessRuleSrc` carrying just `type` and `value`
 */
function toCreateSrc(option: SrcOrDst): CreateAccessRuleSrc {
    return { type: option.type, value: option.value };
}

/**
 * Collect the distinct `src` or `dst` items from a list of instances as filter options
 *
 * @param instances the ruleset's instances
 * @param side whether to collect `src` or `dst` items
 * @returns the deduped {@link SrcOrDst}s, sorted by type rank then by name
 */
function collectFilterOptions(instances: Array<GetRulesetResponseInstance>, side: "src" | "dst"): Array<SrcOrDst> {
    const seen = new Map<string, SrcOrDst>();
    for (const i of instances) {
        const v = i[side];
        const key = `${v.type}:${v.value.uuid}`;
        if (!seen.has(key)) {
            seen.set(key, { type: v.type, name: v.value.name, value: v.value.uuid });
        }
    }
    return [...seen.values()].sort((a, b) => {
        const t = TYPE_ORDER[a.type] - TYPE_ORDER[b.type];
        if (t !== 0) return t;
        return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
    });
}

/**
 * Filter, sort and bucket the instances by the chosen primary axis
 *
 * @param instances the ruleset's instances
 * @param search the current URL filter + group_by state
 * @returns the visible rows in display order
 */
function buildRows(instances: Array<GetRulesetResponseInstance>, search: InstancesSearch): Array<InstanceRow> {
    const filtered = instances.filter(
        (i) =>
            (search.filter_src === null ||
                (i.src.type === search.filter_src.type && i.src.value.uuid === search.filter_src.value)) &&
            (search.filter_dst === null ||
                (i.dst.type === search.filter_dst.type && i.dst.value.uuid === search.filter_dst.value)),
    );
    const sorted = [...filtered].sort((a, b) => compareForGroupBy(a, b, search.group_by));
    const map = new Map<string, InstanceRow>();
    for (const i of sorted) {
        const axis = search.group_by === "src" ? i.src : i.dst;
        const key = `${axis.type}:${axis.value.uuid}`;
        let row = map.get(key);
        if (!row) {
            row = { key, axis, members: [] };
            map.set(key, row);
        }
        row.members.push(i);
    }
    return [...map.values()];
}

/**
 * Comparator for two instances under a given primary group axis
 *
 * Sorts by primary axis (type rank, then name), then by the secondary axis,
 * then by uuid as a stable tiebreaker.
 *
 * @param a the first instance to compare
 * @param b the second instance to compare
 * @param groupBy which axis is the primary grouping axis
 * @returns negative if `a` should come before `b`, positive if after, 0 if equal
 */
function compareForGroupBy(a: GetRulesetResponseInstance, b: GetRulesetResponseInstance, groupBy: GroupBy): number {
    const [pri, sec] = groupBy === "src" ? (["src", "dst"] as const) : (["dst", "src"] as const);
    const priType = TYPE_ORDER[a[pri].type] - TYPE_ORDER[b[pri].type];
    if (priType !== 0) return priType;
    const priName = a[pri].value.name.localeCompare(b[pri].value.name, undefined, { sensitivity: "base" });
    if (priName !== 0) return priName;
    const secType = TYPE_ORDER[a[sec].type] - TYPE_ORDER[b[sec].type];
    if (secType !== 0) return secType;
    const secName = a[sec].value.name.localeCompare(b[sec].value.name, undefined, { sensitivity: "base" });
    if (secName !== 0) return secName;
    return a.uuid.localeCompare(b.uuid);
}

/**
 * Render the appropriate type-specific badge for a {@link FullAccessRuleSrc}
 *
 * @param item the src or dst item to render
 * @param onRemove if set, renders an "x" inside the badge that calls this callback when clicked
 * @returns the matching `<…Badge>` element
 */
function renderBadge(item: FullAccessRuleSrc, onRemove?: () => void): React.ReactNode {
    switch (item.type) {
        case "Tag":
            return <TagBadge tag={item.value} onRemove={onRemove} />;
        case "Group":
            return <GroupBadge group={item.value} onRemove={onRemove} />;
        case "DeviceGroup":
            return <DeviceGroupBadge deviceGroup={item.value} onRemove={onRemove} />;
        case "Subnet":
            return <SubnetBadge subnet={item.value} onRemove={onRemove} />;
    }
}
